import React, { useEffect, useMemo, useState } from "react";

/** Bootstrap-flavored, high-contrast quiz component */
export default function USGeographyQuiz({
  title = "Are You Smarter Than a Bald Eagle? – The Ultimate U.S. Geography Showdown",
}) {
  // --- Quiz Data (USA Geography) ---
  const QUESTIONS = useMemo(
    () => [
      {
        q: "What is the capital of the United States?",
        options: ["New York City", "Washington, D.C.", "Philadelphia", "Boston"],
        a: 1,
        expl:
          "The federal capital is Washington, D.C., established in 1790 on the Potomac River.",
      },
      {
        q: "Which river is the longest in the United States?",
        options: ["Mississippi River", "Missouri River", "Yukon River", "Rio Grande"],
        a: 1,
        expl:
          "The Missouri River is longer than the Mississippi when measured from source to mouth.",
      },
      {
        q: "Which U.S. state has the most coastline?",
        options: ["Florida", "California", "Alaska", "Hawaii"],
        a: 2,
        expl:
          "Alaska’s deeply indented shores give it more coastline than all other states combined.",
      },
      {
        q: "Which city is known as the 'Mile High City'?",
        options: ["Salt Lake City", "Denver", "Albuquerque", "Boise"],
        a: 1,
        expl:
          "Denver’s elevation is approximately one mile (5,280 ft / 1,609 m) above sea level.",
      },
      {
        q: "What is the largest U.S. state by area?",
        options: ["Alaska", "Texas", "California", "Montana"],
        a: 0,
        expl: "Alaska is largest by far; it’s over twice the size of Texas.",
      },
      {
        q: "Which of these states does NOT border the Great Lakes?",
        options: ["Pennsylvania", "Iowa", "Ohio", "Wisconsin"],
        a: 1,
        expl:
          "Iowa is inland; Pennsylvania, Ohio, and Wisconsin touch Great Lakes waters.",
      },
      {
        q: "Which desert covers much of Nevada and parts of Utah and California?",
        options: ["Chihuahuan", "Sonoran", "Mojave", "Great Basin"],
        a: 3,
        expl:
          "The Great Basin Desert is cold and vast, spanning much of the interior West.",
      },
      {
        q: "The Grand Canyon was carved by which river?",
        options: ["Colorado River", "Columbia River", "Arkansas River", "Snake River"],
        a: 0,
        expl:
          "Over millions of years, the Colorado River cut the Grand Canyon in northern Arizona.",
      },
      {
        q: "Which pair are U.S. states that share a border with Canada?",
        options: [
          "Utah & Colorado",
          "Maine & Minnesota",
          "Nebraska & Kansas",
          "Oregon & Nevada",
        ],
        a: 1,
        expl: "Both Maine and Minnesota share land borders with Canada.",
      },
      {
        q: "What is the highest peak in the United States?",
        options: [
          "Mount Rainier",
          "Mount Whitney",
          "Denali (Mount McKinley)",
          "Mount Elbert",
        ],
        a: 2,
        expl:
          "Denali in Alaska stands at 20,310 ft (6,190 m), the highest in North America.",
      },
      {
        q: "Which U.S. region is most associated with tornado frequency?",
        options: [
          "Tornado Alley (Central U.S.)",
          "New England",
          "Pacific Northwest",
          "Deep South only",
        ],
        a: 0,
        expl:
          "‘Tornado Alley’ spans parts of TX/OK/KS/NE/SD/IA/MO—prime for severe thunderstorms.",
      },
      {
        q: "Which state contains the Everglades?",
        options: ["Louisiana", "Georgia", "Florida", "South Carolina"],
        a: 2,
        expl:
          "Everglades National Park protects unique subtropical wetlands in southern Florida.",
      },
    ],
    []
  );

  // --- Helpers ---
  const shuffle = (arr) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  };

  // --- State ---
  const [order, setOrder] = useState(() =>
    shuffle([...Array(QUESTIONS.length).keys()])
  );
  const [idx, setIdx] = useState(0);
  const [score, setScore] = useState(0);
  const [answers, setAnswers] = useState(Array(QUESTIONS.length).fill(null));
  const [checked, setChecked] = useState(Array(QUESTIONS.length).fill(false));

  const total = QUESTIONS.length;
  const qIdx = order[idx];
  const q = QUESTIONS[qIdx];
  const picked = answers[qIdx];

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e) => {
      const k = e.key.toLowerCase();
      if (k === "n") next();
      if (k === "p") prev();
      if (k === "r") restart();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, order, answers, checked, score]);

  const progressPct = Math.round((idx / total) * 100);
  const finishedPct = Math.round(
    (checked.filter(Boolean).length / total) * 100
  );

  function selectOption(i) {
    setAnswers((prev) => {
      const clone = [...prev];
      clone[qIdx] = i;
      return clone;
    });
  }

  function submitCurrent() {
    if (picked === null || picked === undefined) return;
    setChecked((prev) => {
      if (!prev[qIdx]) {
        const c = [...prev];
        c[qIdx] = true;
        if (picked === QUESTIONS[qIdx].a) setScore((s) => s + 1);
        return c;
      }
      return prev;
    });
  }

  function next() {
    if (idx < total - 1) setIdx((i) => i + 1);
  }
  function prev() {
    if (idx > 0) setIdx((i) => i - 1);
  }
  function restart() {
    setOrder(shuffle([...Array(QUESTIONS.length).keys()]));
    setIdx(0);
    setScore(0);
    setAnswers(Array(QUESTIONS.length).fill(null));
    setChecked(Array(QUESTIONS.length).fill(false));
  }

  const atEnd = idx === total - 1;

  return (
    <div className="card shadow-sm border-0" style={{ maxWidth: 900 }}>
      <div className="card-body">
        <div className="d-flex flex-wrap align-items-center justify-content-between mb-2">
          <div>
            <h2 className="h5 mb-1 text-dark">{title}</h2>
            <div className="text-muted small">
              Answer the questions. Explanations appear after you submit.
            </div>
          </div>
          <div className="d-flex align-items-center gap-2 mt-2 mt-sm-0">
            <span className="badge text-bg-light">
              Question <strong>{idx + 1}</strong>/<strong>{total}</strong> · Score:{" "}
              <strong>{score}</strong>
            </span>
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={restart}
              title="Restart (R)"
            >
              Restart
            </button>
          </div>
        </div>

        {/* Progress */}
        <div className="progress mb-2" style={{ height: 8 }}>
          <div
            className="progress-bar"
            role="progressbar"
            style={{ width: `${progressPct}%` }}
            aria-valuenow={progressPct}
            aria-valuemin={0}
            aria-valuemax={100}
          />
        </div>
        <div className="text-muted small mb-3">{progressPct}% complete</div>

        {/* Question */}
        <div className="mb-2 fw-semibold">{q.q}</div>

        {/* Options */}
        <div className="list-group mb-2">
          {q.options.map((opt, i) => {
            const isCorrect = i === q.a;
            const isPicked = picked === i;
            const show = checked[qIdx];

            let className = "list-group-item list-group-item-action d-flex align-items-start";
            let style = { borderRadius: 8, padding: "12px 14px" };

            if (show) {
              if (isCorrect) {
                className += " border-success";
                style = {
                  ...style,
                  backgroundColor: "var(--bs-success-bg-subtle, #e9f7ef)",
                  borderColor: "var(--bs-success, #198754)",
                };
              } else if (isPicked) {
                className += " border-danger";
                style = {
                  ...style,
                  backgroundColor: "var(--bs-danger-bg-subtle, #fdecef)",
                  borderColor: "var(--bs-danger, #dc3545)",
                };
              }
            }

            return (
              <label
                key={i}
                className={className}
                style={style}
              >
                <input
                  type="radio"
                  name={`q-${qIdx}`}
                  className="form-check-input me-2"
                  checked={picked === i}
                  onChange={() => selectOption(i)}
                />
                <div>{opt}</div>
              </label>
            );
          })}
        </div>

        {/* Explanation */}
        {checked[qIdx] && (
          <div className="small text-muted mb-2">
            {picked === q.a ? (
              <span className="text-success fw-semibold">✅ Correct. </span>
            ) : (
              <>
                <span className="text-danger fw-semibold">❌ The correct answer is </span>
                <strong>{q.options[q.a]}</strong>.{" "}
              </>
            )}
            {q.expl}
          </div>
        )}

        {/* Controls */}
        <div className="d-flex justify-content-between align-items-center mt-3">
          <div className="btn-group">
            <button
              className="btn btn-outline-secondary"
              onClick={prev}
              disabled={idx === 0}
              title="Prev (P)"
            >
              ◀ Prev
            </button>
            <button
              className="btn btn-success"
              onClick={submitCurrent}
              title="Submit current question"
            >
              {atEnd ? "Submit" : "Submit"}
            </button>
            <button
              className="btn btn-outline-secondary"
              onClick={next}
              disabled={idx >= total - 1}
              title="Next (N)"
            >
              Next ▶
            </button>
          </div>
          <span className="text-muted small">Tip: N = Next, P = Prev, R = Restart</span>
        </div>

        {/* Summary */}
        {checked.every(Boolean) && (
          <div className="mt-4">
            <h3 className="h6">Your Results</h3>
            <div className="text-muted mb-2">
              Score: <strong>{score}/{total}</strong> ({Math.round((score / total) * 100)}%)
            </div>
            <div className="progress mb-3" style={{ height: 8 }}>
              <div
                className="progress-bar bg-success"
                style={{ width: `${finishedPct}%` }}
              />
            </div>

            <div className="list-group">
              {order.map((qi, i) => {
                const QQ = QUESTIONS[qi];
                const got = answers[qi] === QQ.a;
                return (
                  <div
                    key={qi}
                    className={`list-group-item ${got ? "border-success" : "border-danger"}`}
                    style={{
                      borderRadius: 8,
                      backgroundColor: got
                        ? "var(--bs-success-bg-subtle, #e9f7ef)"
                        : "var(--bs-danger-bg-subtle, #fdecef)",
                      borderColor: got ? "var(--bs-success, #198754)" : "var(--bs-danger, #dc3545)",
                      marginBottom: 8,
                    }}
                  >
                    <div className="fw-semibold">Q{i + 1}: {QQ.q}</div>
                    <div className="small">
                      <em>{got ? "✅ Correct" : "❌ Incorrect"}</em> — Answer:{" "}
                      <strong>{QQ.options[QQ.a]}</strong>
                    </div>
                    <div className="small text-muted">{QQ.expl}</div>
                  </div>
                );
              })}
            </div>

            <div className="d-flex gap-2 mt-3">
              <button className="btn btn-success" onClick={() => setIdx(0)}>
                Review Again
              </button>
              <button className="btn btn-outline-secondary" onClick={restart}>
                Restart Fresh (shuffle)
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}